import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { AuthLayout, Button, ErrorBanner, Field, InfoBanner, TextInput } from "../components/ui.js";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(
        res.error === "INVALID_CREDENTIALS" || res.error === "LOGIN_FAILED"
          ? "The email or password you entered is not correct."
          : "Login is unavailable right now. Please try again.",
      );
    }
    else navigate("/dashboard");
  };

  return (
    <AuthLayout
      eyebrow="Welcome back"
      title="A calmer way to understand your rental portfolio."
      description="Keep your property finances organised, see what needs attention and make your next decision with confidence."
    >
      <div className="auth-card-heading">
        <p className="eyebrow">Your workspace</p>
        <h2>Log in to Nettstay</h2>
        <p>Enter your details to continue to your portfolio.</p>
      </div>
      <form onSubmit={onSubmit} className="form-stack">
        {searchParams.get("reason") === "session" && !error && (
          <InfoBanner message="Please log in to continue to your Nettstay workspace." />
        )}
        {error && <ErrorBanner message={error} />}
        <Field label="Email address">
          <TextInput
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password">
          <div className="password-field">
            <TextInput
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password"
            />
            <button type="button" onClick={() => setShowPassword((current) => !current)}>
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </Field>
        <Button type="submit" disabled={busy} className="btn-block">
          {busy ? "Signing in…" : "Log in"}
        </Button>
        <p className="form-hint form-hint-centered">
          New to Nettstay? <Link to="/register">Create your account</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
